import { formatInTimeZone } from 'date-fns-tz';
import SideBarModal from '../../modal/SideBarModal';
import { LoadedUser, Section, SectionRow } from '../../../interfaces';
import {
  convertOnschedHoursToDateFromTimezone,
  convertShortDayToFull,
  formatAddress,
} from '../../../utils/global/helper';
import { ShortDay, TripWithTimes } from '../../../types/employees';
import useUser from '../../../hooks/global/useUser';

interface Props {
  trip: TripWithTimes | null;
  onClose: () => void;
}

const getDayHoursText = (trip: TripWithTimes, day: ShortDay) => {
  const dayAvailability = trip.weeklyHours?.[day];
  const isClosed =
    !trip.weeklyHours ||
    !dayAvailability ||
    dayAvailability.startTime === dayAvailability.endTime;
  if (isClosed) return 'Closed';
  return `${formatInTimeZone(
    convertOnschedHoursToDateFromTimezone(
      dayAvailability.startTime,
      trip.timezone,
    ),
    trip.timezone,
    'h:mm a',
  )} - ${formatInTimeZone(
    convertOnschedHoursToDateFromTimezone(
      dayAvailability.endTime,
      trip.timezone,
    ),
    trip.timezone,
    'h:mm a',
  )}`;
};

function TripSideBarModal({ trip, onClose }: Props) {
  const { user } = useUser<LoadedUser>();

  if (!trip) return null;

  const employee = user.business.employees.find(
    emp => emp.employeeId === trip.employeeId,
  );
  if (!employee) return null;

  const startDateTime = new Date(trip.startDateTime);
  const endDateTime = new Date(trip.endDateTime);

  const sections: Section[] = [
    ['ADMIN', 'SHOP_MANAGER'].includes(user.role)
      ? {
          name: 'Employee',
          rows: [
            {
              type: 'TEXT',
              label: 'Name',
              value: [
                `${employee.user?.firstName || ''} ${
                  employee.user?.lastName || ''
                }`.trim() || employee.displayName,
              ],
            },
          ],
        }
      : null,
    {
      name: 'Trip Details',
      rows: [
        {
          type: 'TEXT',
          label: 'Books Open',
          value: [trip.booksOpen ? 'Yes' : 'No'],
        },
        {
          type: 'TEXT',
          label: 'Location',
          value: [
            formatAddress([
              trip.studioName,
              trip.streetAddress,
              trip.streetAddress2,
              trip.city,
              trip.state,
              trip.zip,
            ]),
          ],
        },
        {
          type: 'TEXT',
          label: 'Starting Date',
          value: [
            formatInTimeZone(startDateTime, trip.timezone, 'MMMM d, yyyy'),
          ],
        },
        {
          type: 'TEXT',
          label: 'End Date',
          value: [formatInTimeZone(endDateTime, trip.timezone, 'MMMM d, yyyy')],
        },
        {
          type: 'SUBTEXT',
          label: '',
          value: [`All dates are in ${trip.timezone} timezone`],
        },
      ],
    },
    trip.weeklyHours
      ? {
          name: 'Availability',
          rows: [
            {
              type: 'TEXT',
              label: 'Timezone',
              value: [trip.timezone],
            },
            ...Object.keys(trip.weeklyHours).map(
              day =>
                ({
                  label: convertShortDayToFull(day as ShortDay),
                  type: 'TEXT',
                  value: [getDayHoursText(trip, day as ShortDay)],
                }) as SectionRow,
            ),
          ],
          values: ['test'],
        }
      : null,
  ].filter(Boolean) as Section[];

  return (
    <SideBarModal
      modalOpen={!!trip}
      name="Trip"
      sections={sections}
      setModalOpen={onClose}
    />
  );
}

export default TripSideBarModal;
